import {
  BORROWER_DEFAULTS,
  PROGRAM_FIELD_DEFAULTS,
} from "./config";
import { ARM_TERM_YEARS } from "./armTerms";

const ALL_TARGET_PRICE_INCLUDES = ["lockPoint", "adjustment", "compensation"];
const TARGET_PRICE_INCLUDE_MAP = {
  lockpoint: "lockPoint",
  adjustment: "adjustment",
  compensation: "compensation",
};
const SECOND_LIEN_PROGRAMS = ["heloc", "secondMortgage"];

const numberOrUndefined = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// Converts the reused Rates search-state shape to the public PPE request.
// The returned wrapper retains legacy display/filter fields, while
// `pricingRequest` is the only object that may be posted to /data/pricing.
export function formatPpeSearchParams(searchParams) {
  const sp = searchParams || {};
  const n = numberOrUndefined;
  const legacyTypes = (sp.loanTypes || []).map((type) => String(type).toLowerCase());
  let loanProgram = sp.loanProgram;
  if (!loanProgram) {
    if (legacyTypes.some((type) => type.includes("nonqm") || type.includes("non-qm") || type.includes("expanded"))) loanProgram = "nonqm";
    else if (legacyTypes.some((type) => type.includes("fha"))) loanProgram = "fha";
    else if (legacyTypes.some((type) => type.includes("va"))) loanProgram = "va";
    else if (legacyTypes.some((type) => type.includes("usda"))) loanProgram = "usda";
    else loanProgram = "conventional";
  }

  const requestedLien = sp.lienPosition === "second" ? "second" : "first";
  const requestedProgramIsSecondLien = SECOND_LIEN_PROGRAMS.includes(loanProgram);
  const lienPosition = requestedLien === "second" || requestedProgramIsSecondLien
    ? "second"
    : "first";
  if (lienPosition === "second" && !requestedProgramIsSecondLien) {
    loanProgram = "secondMortgage";
  } else if (lienPosition === "first" && requestedProgramIsSecondLien) {
    loanProgram = "conventional";
  }

  const isSecondLienProgram = SECOND_LIEN_PROGRAMS.includes(loanProgram);
  const isHeloc = loanProgram === "heloc";
  const subjectLoanAmount = loanProgram === "heloc"
    ? (n(sp.drawAmount) ?? PROGRAM_FIELD_DEFAULTS.helocDrawAmount)
    : (loanProgram === "secondMortgage"
      ? (n(sp.loanAmount) ?? PROGRAM_FIELD_DEFAULTS.secondMortgageAmount)
      : n(sp.loanAmount));
  const body = {
    lienPosition,
    loanProgram,
    loanPurpose: sp.purpose,
    loanTerm: isHeloc
      ? (["fixed15", "fixed25", "fixed30"].includes(sp.loanTerm) ? sp.loanTerm : "fixed30")
      : sp.loanTerm,
    amortizationType: isHeloc ? "fixed" : (String(sp.loanTerm || "").startsWith("arm") ? "arm" : "fixed"),
    occupancy: sp.occupancy,
    propertyType: sp.property,
    citizenship: sp.citizenship,
    state: sp.state,
    county: sp.county,
    numberOfUnits: n(sp.unit) || 1,
    baseLoanAmount: subjectLoanAmount,
    propertyValue: n(sp.propertyValue),
    creditScore: n(sp.FICO),
    lockDays: n(sp.lockDay) || 30,
    searchBy: String(sp.searchBy || "").toLowerCase().includes("rate") ? "rate" : "price",
  };
  const setIf = (key, value) => {
    if (value !== undefined) body[key] = value;
  };

  // A non-warrantable condo is the condominium propertyType plus this flag;
  // lenders without non-warrantable condo pricing report the request
  // ineligible instead of pricing a warrantable condo.
  if (sp.nonWarrantableCondo) body.nonWarrantableCondo = true;

  setIf("ltv", n(sp.LTV));
  if (!isSecondLienProgram) {
    setIf("cltv", n(sp.CLTV));
    setIf("hcltv", n(sp.HCLTV));
  }
  if (!isSecondLienProgram) {
    setIf("downPayment", n(sp.downPayment));
    setIf("secondLoanAmount", n(sp.secondaryLoanAmount));
  }
  setIf("monthlyIncome", n(sp.monthlyIncome));
  setIf("monthlyDebt", n(sp.monthlyDebt));
  setIf("dti", n(sp.dti));
  if (loanProgram === "fha") {
    const ufmipPct = n(sp.upFrontMIP);
    if (ufmipPct !== undefined) {
      body.ufmip = Math.ceil((body.baseLoanAmount || 0) * ufmipPct / 100);
    }
  }
  setIf("targetPrice", n(sp.targetPrice));
  setIf("targetRate", n(sp.targetRate));
  if (body.searchBy === "price" && Array.isArray(sp.targetPriceType)) {
    const targetPriceIncludes = sp.targetPriceType.includes("")
      ? ALL_TARGET_PRICE_INCLUDES
      : sp.targetPriceType
          .map((value) => TARGET_PRICE_INCLUDE_MAP[String(value).toLowerCase()])
          .filter(Boolean);
    body.targetPriceIncludes = [...new Set(targetPriceIncludes)];
  }

  if (sp.buydown !== undefined && sp.buydown !== null) {
    const normalizedBuydown = String(sp.buydown).trim();
    if (normalizedBuydown && normalizedBuydown.toLowerCase() !== "none") {
      body.buydown = normalizedBuydown;
    }
  }
  if (sp.firstTimeHomeBuyer) body.firstTimeHomeBuyer = true;
  if (sp.selfEmployed || sp.isSelfEmployed) body.selfEmployed = true;
  if (sp.isInterestOnly) body.interestOnly = true;
  if (sp.isImpound !== undefined) body.impound = !!sp.isImpound;

  const compensation = String(sp.compensationType || "");
  if (compensation === "lender_paid" || compensation === "lenderPaid") {
    body.compensation = "lenderPaid";
  } else if (compensation === "borrower_paid" || compensation === "borrowerPaid") {
    body.compensation = "borrowerPaid";
    setIf("compensationPercentage", n(sp.compensationPercent));
  }
  if (Array.isArray(sp.ausTypes) && sp.ausTypes.length) {
    body.ausTypes = sp.ausTypes.map((value) => String(value));
  }
  if (body.amortizationType === "arm") {
    setIf("armFixedTerm", ARM_TERM_YEARS[body.loanTerm] || n(sp.armFixedTerm));
  }

  setIf("appraisedValue", n(sp.appraisedValue));
  if (!isSecondLienProgram) setIf("totalLoanAmount", n(sp.totalLoanAmount));
  setIf("cashOutAmount", n(sp.cashOutAmount));
  if (!isSecondLienProgram) {
    setIf("cltv", n(sp.cltvInput));
    setIf("hcltv", n(sp.hcltvInput));
  }
  if (loanProgram === "heloc") {
    setIf("lineAmount", n(sp.lineAmount));
    setIf("drawAmount", n(sp.drawAmount));
    setIf("drawTermMonths", n(sp.drawTermMonths));
  }
  if (isSecondLienProgram) {
    setIf("primaryMortgageAmount", n(sp.primaryMortgageAmount));
    // The backend recomputes second-lien ratios from the amount structure, but
    // secondMortgage's public schema requires CLTV. These values come from the
    // same frontend-derived snapshot used to render the result filters.
    setIf("cltv", n(sp.CLTV));
    setIf("hcltv", n(sp.HCLTV));
  }
  if (loanProgram === "va" && !sp.vaExemptFundingFee) setIf("fundingFee", n(sp.fundingFee));
  if (loanProgram === "va" && sp.vaSubsequentUse) body.vaSubsequentUse = true;
  if (loanProgram === "va" && sp.vaExemptFundingFee) body.vaExemptFundingFee = true;
  if (loanProgram === "usda") setIf("guaranteeFee", n(sp.guaranteeFee));
  setIf("reserves", n(sp.reserves) ?? BORROWER_DEFAULTS.reserves);
  setIf("propertiesFinanced", n(sp.propertiesFinanced) ?? BORROWER_DEFAULTS.propertiesFinanced);
  if (sp.nonOccupantCoBorrower) body.nonOccupantCoBorrower = true;
  if (sp.itin) body.itin = true;
  if (n(sp.numberOfUnits)) body.numberOfUnits = n(sp.numberOfUnits);
  if (sp.propertyZip) body.propertyZip = String(sp.propertyZip);
  setIf("prepaymentPenalty", n(sp.prepaymentPenalty));
  if (sp.interestOnlyNew) body.interestOnly = true;
  if (sp.nonQm) body.nonQm = sp.nonQm;

  if (loanProgram === "va" && !body.vaExemptFundingFee && body.fundingFee === undefined) {
    body.fundingFee = PROGRAM_FIELD_DEFAULTS.vaFundingFee;
  }
  if (loanProgram === "usda" && body.guaranteeFee === undefined) {
    body.guaranteeFee = PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee;
  }
  if (loanProgram === "heloc" && body.lineAmount === undefined) {
    body.lineAmount = PROGRAM_FIELD_DEFAULTS.helocLineAmount;
  }
  if (loanProgram === "heloc" && body.drawAmount === undefined) {
    body.drawAmount = PROGRAM_FIELD_DEFAULTS.helocDrawAmount;
  }
  if (loanProgram === "heloc" && body.drawTermMonths === undefined) {
    body.drawTermMonths = PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths;
  }
  if (isSecondLienProgram && body.primaryMortgageAmount === undefined) {
    body.primaryMortgageAmount = PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount;
  }

  return {
    ...sp,
    ...body,
    loanTerms: body.loanTerm,
    loanAmount: body.baseLoanAmount,
    pricingRequest: body,
  };
}
